import { isPlainObject, sanitizeValue } from '@labrav/common'

export type ReduxActionSummary = {
  type: string
  time: string
  // Small, sanitized hints only (no payload dumps)
  meta?: Record<string, unknown>
}

export type AuthType = 'auth0' | 'salesforce-canvas' | 'external-app'

export type UserTelemetryContext = {
  partnerId: string | null
  partnerUserId: string | null
  roles: string[]
  authType: AuthType
}

export type ReduxTelemetrySnapshot = {
  actions: ReduxActionSummary[]
  userContext: UserTelemetryContext
}

let getStateFn: (() => unknown) | undefined
let currentAuthType: AuthType = 'auth0'
const recentActions: ReduxActionSummary[] = []

const MAX_ACTIONS = 50

export function setReduxTelemetryStateGetter(getter: () => unknown): void {
  getStateFn = getter
}

export function setTelemetryAuthType(type: AuthType): void {
  currentAuthType = type
}

export function recordReduxAction(action: {
  type?: unknown
  meta?: unknown
}): void {
  const type = typeof action.type === 'string' ? action.type : 'unknown_action'
  const meta = isPlainObject(action.meta)
    ? (sanitizeValue(action.meta, 2) as Record<string, unknown>)
    : undefined

  recentActions.push({
    type,
    time: new Date().toISOString(),
    ...(meta ? { meta } : {}),
  })

  if (recentActions.length > MAX_ACTIONS) {
    recentActions.splice(0, recentActions.length - MAX_ACTIONS)
  }
}

function extractUserContext(rawState: unknown): UserTelemetryContext {
  const s = rawState as Record<string, any>
  const partnerId = s?.AccountData?.user?.partnerData?.partnerId ?? null
  const partnerUserId = s?.userProfile?.userProfile?.id ?? null
  const roles: string[] = Array.isArray(s?.userProfile?.userProfile?.roles)
    ? s.userProfile.userProfile.roles
    : []
  return { partnerId, partnerUserId, roles, authType: currentAuthType }
}

export function getReduxTelemetrySnapshot(): ReduxTelemetrySnapshot | null {
  if (!getStateFn) return null
  const rawState = getStateFn()
  const actions = recentActions.slice()
  const userContext = extractUserContext(rawState)

  return { actions, userContext }
}
